import { getAuthToken } from '../state/store.js';
import {
  getCallcenterSummary,
  getCallcenterReviews,
  getCallcenterAgents,
  getCallcenterExportUrl,
} from '../api/client.js';

let container;
let fromInput, toInput, agentSelect, ratingSelect, refreshBtn, exportBtn;
let summaryGrid, agentsTable, reasonsChart, reviewsList;
let expandedReviewId = null;

export function initCallcenterAnalytics() {
  container = document.getElementById('ccAnalyticsTab');
  if (!container) return;

  fromInput   = document.getElementById('ccFrom');
  toInput     = document.getElementById('ccTo');
  agentSelect = document.getElementById('ccAgent');
  ratingSelect = document.getElementById('ccRating');
  refreshBtn  = document.getElementById('ccRefreshBtn');
  exportBtn   = document.getElementById('ccExportBtn');
  summaryGrid = document.getElementById('ccSummary');
  agentsTable = document.getElementById('ccAgentsTable');
  reasonsChart = document.getElementById('ccReasons');
  reviewsList = document.getElementById('ccReviewsList');

  if (!refreshBtn) return;

  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  fromInput.value = thirtyDaysAgo.toISOString().split('T')[0];
  toInput.value = today.toISOString().split('T')[0];

  refreshBtn.addEventListener('click', () => loadCallcenterAnalytics());
  exportBtn.addEventListener('click', () => handleExport());
}

export async function loadCallcenterAnalytics() {
  if (!summaryGrid) return;

  const fromDate = fromInput.value || null;
  const toDate = toInput.value ? toInput.value + 'T23:59:59' : null;
  const agent = agentSelect.value || null;
  const rating = ratingSelect.value || null;

  const spinner = '<div class="analytics-loading"><div class="spinner"></div></div>';
  summaryGrid.innerHTML = spinner;
  if (agentsTable) agentsTable.innerHTML = spinner;
  if (reasonsChart) reasonsChart.innerHTML = '';
  if (reviewsList) reviewsList.innerHTML = spinner;

  try {
    const [summary, agents, reviews] = await Promise.all([
      getCallcenterSummary(fromDate, toDate, agent),
      getCallcenterAgents(fromDate, toDate),
      getCallcenterReviews(fromDate, toDate, agent, rating),
    ]);

    renderSummary(summary);
    renderAgents(agents);
    renderReasons(summary.bad_reasons || []);
    renderReviews(reviews);
    populateAgentFilter(agents);
  } catch (error) {
    summaryGrid.innerHTML = `<div class="analytics-no-data">Fehler: ${escapeHtml(error.message)}</div>`;
    if (reviewsList) reviewsList.innerHTML = '';
  }
}

// ---------------------------------------------------------------------------
// Summary metric cards
// ---------------------------------------------------------------------------

function renderSummary(data) {
  const metrics = [
    { label: 'Bewertungen', value: data.total_evaluations ?? 0, cls: 'metric-neutral' },
    { label: 'Gut', value: data.good ?? 0, cls: 'metric-positive' },
    { label: 'Schlecht', value: data.bad ?? 0, cls: 'metric-negative' },
    {
      label: 'Gut-Quote',
      value: data.good_rate != null ? data.good_rate + '%' : '--',
      cls: data.good_rate == null ? 'metric-neutral' : data.good_rate >= 70 ? 'metric-positive' : data.good_rate >= 40 ? 'metric-warn' : 'metric-negative',
    },
    {
      label: 'Anpassungsrate',
      value: data.modification_rate != null ? data.modification_rate + '%' : '--',
      cls: 'metric-neutral',
    },
    {
      label: 'Ø Änderungsgrad',
      value: data.avg_change_ratio != null ? (data.avg_change_ratio * 100).toFixed(1) + '%' : '--',
      cls: 'metric-neutral',
    },
    {
      label: 'Ø Antwortzeit',
      value: formatDurationMs(data.avg_response_time_ms),
      cls: responseTimeCls(data.avg_response_time_ms),
      title: 'Richtwerte: bis 5s gut, bis 15s Hinweis, darüber kritisch',
    },
    {
      label: 'Kopiert',
      value: data.copied_count ?? 0,
      cls: 'metric-neutral',
    },
    {
      label: 'Kopierrate',
      value: data.copy_rate != null ? data.copy_rate + '%' : '--',
      cls: data.copy_rate == null ? 'metric-neutral' : data.copy_rate >= 60 ? 'metric-positive' : 'metric-warn',
    },
  ];

  summaryGrid.innerHTML = metrics
    .map(m => `
      <div class="analytics-card analytics-card-secondary"${m.title ? ` title="${m.title}"` : ''}>
        <div class="metric-value ${m.cls}">${m.value}</div>
        <div class="metric-label">${m.label}</div>
      </div>`)
    .join('');
}

// ---------------------------------------------------------------------------
// Per-agent breakdown table
// ---------------------------------------------------------------------------

function renderAgents(agents) {
  if (!agentsTable) return;

  if (!agents || agents.length === 0) {
    agentsTable.innerHTML = '<div class="analytics-no-data">Keine Agent-Daten vorhanden.</div>';
    return;
  }

  const rows = agents.map(a => `
    <tr>
      <td class="cca-cell-agent">${escapeHtml(a.agent_alias)}</td>
      <td class="cca-cell-num">${a.total}</td>
      <td class="cca-cell-num cca-good">${a.good}</td>
      <td class="cca-cell-num cca-bad">${a.bad}</td>
      <td class="cca-cell-num">${a.good_rate != null ? a.good_rate + '%' : '--'}</td>
      <td class="cca-cell-num">${a.avg_change_ratio != null ? (a.avg_change_ratio * 100).toFixed(1) + '%' : '--'}</td>
      <td class="cca-cell-num">${formatDurationMs(a.avg_response_time_ms)}</td>
      <td class="cca-cell-num">${a.copy_rate != null ? a.copy_rate + '%' : '--'}</td>
      <td class="cca-cell-date">${formatDate(a.last_review)}</td>
    </tr>`).join('');

  agentsTable.innerHTML = `
    <table class="cca-table">
      <thead>
        <tr>
          <th>Agent</th>
          <th>Gesamt</th>
          <th>Gut</th>
          <th>Schlecht</th>
          <th>Gut-Quote</th>
          <th>Ø Änderung</th>
          <th>Ø Antwortzeit</th>
          <th>Kopierrate</th>
          <th>Letzte Aktivität</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>`;
}

function populateAgentFilter(agents) {
  if (!agentSelect) return;
  const current = agentSelect.value;
  const opts = ['<option value="">Alle Agenten</option>'];
  for (const a of agents) {
    const sel = a.agent_alias === current ? ' selected' : '';
    opts.push(`<option value="${escapeHtml(a.agent_alias)}"${sel}>${escapeHtml(a.agent_alias)}</option>`);
  }
  agentSelect.innerHTML = opts.join('');
}

// ---------------------------------------------------------------------------
// Bad reasons horizontal bar chart
// ---------------------------------------------------------------------------

const REASON_LABELS = {
  'incomplete': 'Unvollständig',
  'incorrect': 'Falsch',
  'tone': 'Ton/Stil',
  'policy': 'Richtlinie',
  'other': 'Sonstiges',
};

function renderReasons(reasons) {
  if (!reasonsChart) return;

  if (!reasons || reasons.length === 0) {
    reasonsChart.innerHTML = '<div class="analytics-no-data">Keine Grunddaten.</div>';
    return;
  }

  const maxCount = Math.max(...reasons.map(r => r.count), 1);
  reasonsChart.innerHTML = reasons.map(r => {
    const widthPct = (r.count / maxCount) * 100;
    const label = REASON_LABELS[r.reason] || r.reason || 'Unbekannt';
    return `
      <div class="peak-row" title="${escapeHtml(label)}: ${r.count}">
        <span class="peak-hour-label" style="min-width:100px">${escapeHtml(label)}</span>
        <div class="peak-bar-track">
          <div class="peak-bar cca-reason-bar" style="width:${widthPct}%"></div>
        </div>
        <span class="peak-count">${r.count}</span>
      </div>`;
  }).join('');
}

// ---------------------------------------------------------------------------
// Reviews table with expandable diff
// ---------------------------------------------------------------------------

function renderReviews(reviews) {
  if (!reviewsList) return;

  if (!reviews || reviews.length === 0) {
    reviewsList.innerHTML = '<div class="analytics-no-data cca-empty">Keine Bewertungen im Zeitraum.</div>';
    return;
  }

  reviewsList.innerHTML = reviews.map(r => {
    const isExpanded = expandedReviewId === r.id;
    const ratingCls = r.rating === 'good' ? 'cca-badge-good' : 'cca-badge-bad';
    const ratingLabel = r.rating === 'good' ? 'Gut' : 'Schlecht';
    const changeStr = r.change_ratio != null ? (r.change_ratio * 100).toFixed(1) + '%' : '--';
    const timeStr = formatDurationMs(r.response_time_ms);
    const reasonStr = r.bad_reason ? (REASON_LABELS[r.bad_reason] || r.bad_reason) : '';
    const question = r.question_text || '(Frage nicht verfügbar)';

    let diffHtml = '';
    if (isExpanded) {
      diffHtml = buildDiffPanel(r.original_answer, r.final_answer);
    }

    return `
      <div class="cca-review-row${isExpanded ? ' cca-review-expanded' : ''}" data-review-id="${r.id}">
        <div class="cca-review-header" data-review-toggle="${r.id}">
          <div class="cca-review-left">
            <span class="cca-badge ${ratingCls}">${ratingLabel}</span>
            <span class="cca-review-question">${escapeHtml(question)}</span>
          </div>
          <div class="cca-review-right">
            ${reasonStr ? `<span class="cca-reason-tag">${escapeHtml(reasonStr)}</span>` : ''}
            <span class="cca-change-tag" title="Änderungsgrad">${changeStr}</span>
            ${timeStr !== '--' ? `<span class="cca-time-tag" title="Antwortzeit">${timeStr}</span>` : ''}
            <span class="cca-review-agent">${escapeHtml(r.agent_alias || '--')}</span>
            <span class="cca-review-date">${formatDate(r.created_at)}</span>
            <span class="cca-expand-icon">${isExpanded ? '&#9650;' : '&#9660;'}</span>
          </div>
        </div>
        ${diffHtml}
      </div>`;
  }).join('');

  reviewsList.querySelectorAll('[data-review-toggle]').forEach(el => {
    el.addEventListener('click', () => {
      const id = Number(el.dataset.reviewToggle);
      expandedReviewId = expandedReviewId === id ? null : id;
      renderReviews(reviews);
    });
  });
}

// ---------------------------------------------------------------------------
// Side-by-side diff view
// ---------------------------------------------------------------------------

function buildDiffPanel(original, final) {
  if (!original && !final) {
    return '<div class="cca-diff-panel"><div class="analytics-no-data">Keine Antwort-Daten.</div></div>';
  }

  const origLines = (original || '').split('\n');
  const finalLines = (final || '').split('\n');
  const maxLen = Math.max(origLines.length, finalLines.length);

  let origHtml = '';
  let finalHtml = '';

  for (let i = 0; i < maxLen; i++) {
    const oLine = origLines[i] ?? '';
    const fLine = finalLines[i] ?? '';
    const changed = oLine !== fLine;

    origHtml  += `<div class="cca-diff-line${changed ? ' cca-diff-del' : ''}">${escapeHtml(oLine) || '&nbsp;'}</div>`;
    finalHtml += `<div class="cca-diff-line${changed ? ' cca-diff-add' : ''}">${escapeHtml(fLine) || '&nbsp;'}</div>`;
  }

  return `
    <div class="cca-diff-panel">
      <div class="cca-diff-col">
        <div class="cca-diff-heading">Original</div>
        <div class="cca-diff-body">${origHtml}</div>
      </div>
      <div class="cca-diff-col">
        <div class="cca-diff-heading">Bearbeitet</div>
        <div class="cca-diff-body">${finalHtml}</div>
      </div>
    </div>`;
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

function handleExport() {
  const fromDate = fromInput.value || null;
  const toDate   = toInput.value ? toInput.value + 'T23:59:59' : null;
  const agent    = agentSelect.value || null;
  const rating   = ratingSelect.value || null;

  const url = getCallcenterExportUrl(fromDate, toDate, agent, rating);

  const token = getAuthToken();
  fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
    .then(res => {
      if (!res.ok) throw new Error('Export fehlgeschlagen');
      return res.blob();
    })
    .then(blob => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'callcenter_reviews.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    })
    .catch(err => {
      alert('CSV-Export fehlgeschlagen: ' + err.message);
    });
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function formatDate(isoString) {
  if (!isoString) return '--';
  try {
    return new Date(isoString).toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch {
    return isoString;
  }
}

function formatDurationMs(value) {
  if (value == null || Number.isNaN(Number(value))) return '--';
  const ms = Number(value);
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function responseTimeCls(ms) {
  const value = Number(ms);
  if (!Number.isFinite(value)) return 'metric-neutral';
  if (value <= 5000) return 'metric-positive';
  if (value <= 15000) return 'metric-warn';
  return 'metric-negative';
}
